/**
 * PitchPrint Swap Controller
 *
 * Communicates with the PitchPrint iframe via postMessage to trigger
 * design swaps programmatically without user interaction.
 *
 * Usage:
 *   // Initialize
 *   const swapController = new PitchPrintSwapController();
 *
 *   // Wait for ready
 *   swapController.waitForReady().then(() => {
 *     // Swap page 0 to design at index 5
 *     swapController.swapPage(5, 0).then(result => {
 *       console.log('Swap complete:', result);
 *     });
 *   });
 */

(function() {
  'use strict';

  var DEBUG = false;
  var log = function() {
    if (DEBUG || window.PITCHPRINT_DEBUG) console.log.apply(console, ['[SwapController]'].concat(Array.prototype.slice.call(arguments)));
  };

  function PitchPrintSwapController(options) {
    options = options || {};

    this.iframe = null;
    this.iframeWindow = null;
    this.isReady = false;
    this.pendingRequests = {};
    this.readyPromise = null;
    this.readyResolve = null;
    this.onReadyCallbacks = [];
    this.pageLoaderState = null;

    // Set up message listener
    this._boundMessageHandler = this._handleMessage.bind(this);
    window.addEventListener('message', this._boundMessageHandler);

    // Try to find iframe immediately
    this._findIframe();

    // Also set up observer to detect when iframe appears
    this._setupIframeObserver();

    log('SwapController initialized');
  }

  PitchPrintSwapController.prototype._findIframe = function() {
    // Look for PitchPrint iframe
    var iframe = document.querySelector('iframe[src*="pitchprint"]') ||
                 document.querySelector('iframe#pp-iframe') ||
                 document.querySelector('iframe[name="pp-frame"]') ||
                 document.querySelector('.pitchprint-container iframe');

    if (iframe) {
      this.iframe = iframe;
      this.iframeWindow = iframe.contentWindow;
      log('Found PitchPrint iframe:', iframe);

      // Send ping to check if API is ready
      this._sendPing();
    }

    return !!iframe;
  };

  PitchPrintSwapController.prototype._setupIframeObserver = function() {
    var self = this;

    // Watch for iframe being added to DOM
    var observer = new MutationObserver(function(mutations) {
      if (!self.iframe) {
        self._findIframe();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Also check periodically for a short time
    var attempts = 0;
    var checkInterval = setInterval(function() {
      attempts++;
      if (self._findIframe() || attempts > 50) {
        clearInterval(checkInterval);
      }
    }, 200);
  };

  PitchPrintSwapController.prototype._sendPing = function() {
    var self = this;
    var pingId = 'ping-' + Date.now();

    this._sendMessage('ping', {}, pingId).then(function(response) {
      if (response && response.ready) {
        self._setReady();
      }
    }).catch(function() {
      // Retry after a delay
      setTimeout(function() {
        if (!self.isReady && self.iframeWindow) {
          self._sendPing();
        }
      }, 500);
    });
  };

  PitchPrintSwapController.prototype._setReady = function() {
    if (this.isReady) return;

    this.isReady = true;
    log('API is ready');

    // Resolve ready promise
    if (this.readyResolve) {
      this.readyResolve();
    }

    // Call callbacks
    this.onReadyCallbacks.forEach(function(cb) {
      try { cb(); } catch(e) { console.error(e); }
    });
    this.onReadyCallbacks = [];

    // Fetch initial state
    this.getPageLoaderState();
  };

  PitchPrintSwapController.prototype._handleMessage = function(event) {
    if (!event.data || typeof event.data !== 'object') return;
    if (event.data.source !== 'popsigns-swap-api') return;

    var action = event.data.action;
    var requestId = event.data.requestId;
    var data = event.data.data || {};

    log('Received message:', action, data);

    // Handle api-ready notification
    if (action === 'api-ready') {
      this._setReady();
      return;
    }

    // v3.15.30: Handle editor-cancelled notification - clear cache and reload for fresh state
    if (action === 'editor-cancelled') {
      log('v3.15.30: Editor cancelled - clearing design cache and reloading page');

      // Clear design template cache to ensure fresh templates on next preview
      // Note: We don't clear pprint-projects as that slows down PitchPrint reload
      // The page reload itself gives PitchPrint a fresh state
      if (window.DesignTemplateCache && typeof window.DesignTemplateCache.clear === 'function') {
        window.DesignTemplateCache.clear();
        log('v3.15.30: Design template cache cleared');
      }

      setTimeout(function() {
        window.location.reload();
      }, 100);
      return;
    }

    // Resolve pending request
    if (requestId && this.pendingRequests[requestId]) {
      var pending = this.pendingRequests[requestId];
      delete this.pendingRequests[requestId];

      if (action.includes('error') || action.includes('timeout')) {
        pending.reject(data);
      } else {
        pending.resolve(data);
      }
    }

    // Update cached state
    if (action === 'state') {
      this.pageLoaderState = data;
    }
  };

  PitchPrintSwapController.prototype._sendMessage = function(action, data, requestId) {
    var self = this;
    requestId = requestId || (action + '-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9));

    return new Promise(function(resolve, reject) {
      if (!self.iframeWindow) {
        reject(new Error('Iframe not found'));
        return;
      }

      // Store pending request
      self.pendingRequests[requestId] = {
        resolve: resolve,
        reject: reject,
        timestamp: Date.now()
      };

      // Send message
      self.iframeWindow.postMessage({
        source: 'popsigns-swap-api',
        action: action,
        data: data,
        requestId: requestId
      }, '*');

      // Timeout - increased to 20s to allow SwapAPI's 15s internal timeout to complete
      setTimeout(function() {
        if (self.pendingRequests[requestId]) {
          delete self.pendingRequests[requestId];
          reject(new Error('Request timeout: ' + action));
        }
      }, 20000);
    });
  };

  /**
   * Wait for the API to be ready
   * @returns {Promise}
   */
  PitchPrintSwapController.prototype.waitForReady = function() {
    var self = this;

    if (this.isReady) {
      return Promise.resolve();
    }

    if (!this.readyPromise) {
      this.readyPromise = new Promise(function(resolve) {
        self.readyResolve = resolve;
      });
    }

    return this.readyPromise;
  };

  /**
   * Register a callback for when API is ready
   * @param {Function} callback
   */
  PitchPrintSwapController.prototype.onReady = function(callback) {
    if (this.isReady) {
      callback();
    } else {
      this.onReadyCallbacks.push(callback);
    }
  };

  /**
   * Swap a page in the design
   * @param {number} pageIndex - Index of the template page from Collection Library (0-based)
   * @param {number} [targetPosition] - Which position in the design to swap (0=front, 1=back). Defaults to current page.
   * @returns {Promise<Object>} - Resolves with swap result
   */
  PitchPrintSwapController.prototype.swapPage = function(pageIndex, targetPosition) {
    log('Swapping to page index:', pageIndex, 'at position:', targetPosition);

    var messageData = {
      pageIndex: pageIndex,
      targetPosition: targetPosition
    };

    return this._sendMessage('swap-page', messageData);
  };

  /**
   * Add a new page from Page Loader template (for double-sided products)
   * @param {number} pageIndex - Index of the template page from metafield (fallback)
   * @param {string} [previewUrl] - Preview URL to match template by thumbnail (preferred)
   * @returns {Promise<Object>} - Resolves with add result including new page count
   */
  PitchPrintSwapController.prototype.addPage = function(pageIndex, previewUrl) {
    log('Adding page from template index:', pageIndex, 'previewUrl:', previewUrl);

    return this._sendMessage('add-page', {
      pageIndex: pageIndex,
      previewUrl: previewUrl
    });
  };

  /**
   * Add a page by loading another design directly (v3.8)
   * Uses the individual template's designId instead of Page Loader index matching
   * @param {string} designId - The PitchPrint designId of the template to add as page 2
   * @returns {Promise<Object>} - Resolves with add result including page count
   */
  PitchPrintSwapController.prototype.addPageFromDesign = function(designId) {
    log('Adding page from designId:', designId);

    return this._sendMessage('add-page-from-design', {
      designId: designId
    });
  };

  /**
   * Add a page by finding it in Page Loader via pageIndex (v3.13)
   * v3.13 uses pageIndex directly to calculate collectionPage = pageIndex + 1
   * This aligns with F01-F15 numbering where individual designs match collection pages
   * @param {string} designId - The PitchPrint designId of the template (for logging/fallback)
   * @param {string} title - The template title (for logging/fallback)
   * @param {number} pageIndex - The pageIndex from metafield (0-based, maps to collectionPage = pageIndex + 1)
   * @returns {Promise<Object>} - Resolves with add result including matched index and method
   */
  PitchPrintSwapController.prototype.addPageByDesignId = function(designId, title, pageIndex) {
    log('Adding page by pageIndex (v3.13):', designId, 'title:', title, 'pageIndex:', pageIndex);

    return this._sendMessage('add-page-by-design-id', {
      designId: designId,
      title: title,
      pageIndex: pageIndex
    });
  };

  /**
   * Add a page and then save the project (for double-sided products)
   * @param {number} pageIndex - Index of the template page to add
   * @returns {Promise<Object>} - Resolves with projectId and add result
   */
  PitchPrintSwapController.prototype.addPageAndSave = function(pageIndex) {
    var self = this;

    log('Add page and save:', pageIndex);

    return this.addPage(pageIndex).then(function(addResult) {
      log('Page added, saving...');
      return self.saveProject().then(function(saveResult) {
        return {
          add: addResult,
          save: saveResult,
          projectId: saveResult.projectId,
          previews: saveResult.previews
        };
      });
    });
  };

  /**
   * Get the current state of the Page Loader
   * @returns {Promise<Object>} - State object with availablePages, currentDesignId, etc.
   */
  PitchPrintSwapController.prototype.getPageLoaderState = function() {
    var self = this;

    return this._sendMessage('get-page-loader-state', {}).then(function(state) {
      self.pageLoaderState = state;
      return state;
    });
  };

  /**
   * Save the current project
   * @returns {Promise<Object>} - Resolves with projectId and previews
   */
  PitchPrintSwapController.prototype.saveProject = function() {
    log('Requesting project save');
    return this._sendMessage('save-project', {});
  };

  /**
   * Load a design by ID and save it (atomic operation)
   * This is more reliable than timing-based save because it waits for
   * the design-loaded event inside the iframe before triggering save.
   * @param {string} designId - The PitchPrint design ID to load
   * @returns {Promise<Object>} - Resolves with projectId and designId
   */
  PitchPrintSwapController.prototype.loadAndSave = function(designId) {
    log('Requesting load-and-save for designId:', designId);
    return this._sendMessage('load-and-save', { designId: designId });
  };

  /**
   * Load a customized design via load-design event (v3.15)
   * Uses DesignModifier to apply customizations to cached design JSON
   * @param {Object} designJson - The design JSON (with pages array)
   * @param {Object} customizations - Customizations to apply
   * @param {number} collectionPage - Collection page number for caching
   * @param {string} shape - Shape type for caching
   * @returns {Promise<Object>} - Resolves with success status
   */
  PitchPrintSwapController.prototype.loadCustomizedDesign = function(designJson, customizations, collectionPage, shape) {
    log('Loading customized design:', collectionPage, shape, customizations);
    return this._sendMessage('load-customized-design', {
      designJson: designJson,
      customizations: customizations,
      collectionPage: collectionPage,
      shape: shape
    });
  };

  /**
   * Apply customizations to a double-sided design (both pages)
   * v3.15.51: New method for double-sided customization support
   * Called after both front and back swaps complete, applies customizations to all pages
   * @param {Object} customizations - Customizations to apply to all pages
   * @param {number} collectionPage - Collection page for logging
   * @param {string} shape - Shape type for logging
   * @returns {Promise<Object>} - Resolves with per-page success status
   */
  PitchPrintSwapController.prototype.loadCustomizedDesignDoubleSided = function(customizations, collectionPage, shape) {
    log('v3.15.51: Applying customizations to double-sided design:', collectionPage, shape, customizations);
    return this._sendMessage('load-customized-design-double-sided', {
      customizations: customizations,
      collectionPage: collectionPage,
      shape: shape
    });
  };

  /**
   * Extract current design from runtime and cache it (v3.15)
   * Useful after a swap to cache the design for future customization
   * @param {number} collectionPage - Collection page number for cache key
   * @param {string} shape - Shape type for cache key
   * @returns {Promise<Object>} - Resolves with cached design info
   */
  PitchPrintSwapController.prototype.extractDesignCache = function(collectionPage, shape) {
    log('Extracting design cache:', collectionPage, shape);
    return this._sendMessage('extract-design-cache', {
      collectionPage: collectionPage,
      shape: shape
    });
  };

  /**
   * Swap a page and then save the project
   * @param {number} pageIndex - Index of the template page
   * @param {number} [targetPosition] - Position in design to swap
   * @returns {Promise<Object>} - Resolves with projectId and swap result
   */
  PitchPrintSwapController.prototype.swapAndSave = function(pageIndex, targetPosition) {
    var self = this;

    log('Swap and save:', pageIndex, 'at position:', targetPosition);

    return this.swapPage(pageIndex, targetPosition).then(function(swapResult) {
      log('Swap complete, saving...');
      return self.saveProject().then(function(saveResult) {
        return {
          swap: swapResult,
          save: saveResult,
          projectId: saveResult.projectId,
          previews: saveResult.previews
        };
      });
    });
  };

  /**
   * Get cached page loader state
   * @returns {Object|null}
   */
  PitchPrintSwapController.prototype.getCachedState = function() {
    return this.pageLoaderState;
  };

  /**
   * Check if a page index is available
   * @param {number} pageIndex
   * @returns {boolean}
   */
  PitchPrintSwapController.prototype.isPageAvailable = function(pageIndex) {
    if (!this.pageLoaderState || !this.pageLoaderState.availablePages) {
      return false;
    }
    return this.pageLoaderState.availablePages.some(function(p) {
      return p.index === pageIndex;
    });
  };

  /**
   * Cleanup - remove event listeners
   */
  PitchPrintSwapController.prototype.destroy = function() {
    window.removeEventListener('message', this._boundMessageHandler);
    this.pendingRequests = {};
    log('SwapController destroyed');
  };

  // Create singleton instance
  var instance = null;

  window.PitchPrintSwapController = PitchPrintSwapController;

  // Auto-initialize singleton
  window.getPitchPrintSwapController = function() {
    if (!instance) {
      instance = new PitchPrintSwapController();
    }
    return instance;
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      window.getPitchPrintSwapController();
    });
  } else {
    window.getPitchPrintSwapController();
  }

  log('PitchPrint Swap Controller script loaded');
})();
